import React from "react";
import { Link } from "react-router-dom";
import CircaWordmark from "./CircaWordmark";
import CircaMark from "./CircaMark";
import ClosedBetaInterestChrome from "@/components/ClosedBetaInterestBanner";

const BENEFITS = [
  {
    title: "Your private workplace",
    body: "Teams, updates, messages, and catch-up—so you stay aligned without chasing every thread.",
  },
  {
    title: "Stay close beyond one org",
    body: "Circa Social and communities keep you connected to the wider circle around your work.",
  },
  {
    title: "Sign in the way your org works",
    body: "SSO on Business and Enterprise.",
  },
];

/**
 * Shared public auth layout for sign-in and sign-up.
 */
const AuthShell = ({ title, subtitle, children, footer }) => (
  <ClosedBetaInterestChrome>
    {({ openDialog }) => (
      <div className="min-h-screen w-full circa-orbit-bg">
        <div className="relative mx-auto grid min-h-[calc(100vh-0px)] w-full max-w-6xl lg:grid-cols-2">
          <aside className="relative z-10 hidden flex-col justify-between border-r border-border/60 p-10 lg:flex xl:p-14">
            <CircaWordmark showTagline />

            <div className="max-w-md space-y-8">
              <div className="space-y-3">
                <h1 className="text-4xl font-semibold tracking-tight text-foreground">
                  Stay close to what matters.
                </h1>
                <p className="text-base leading-relaxed text-muted-foreground">
                  For everyone who needs to stay aligned and in the loop—from
                  small teams and schools to nonprofits and large organizations.
                </p>
              </div>

              <ul className="space-y-5">
                {BENEFITS.map((item) => (
                  <li key={item.title} className="flex gap-3">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                      <CircaMark className="h-4 w-4" decorative />
                    </span>
                    <div>
                      <p className="font-medium text-foreground">{item.title}</p>
                      <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">
                        {item.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-2 text-xs text-muted-foreground/80">
              <p>
                Circa - from Latin <em>circa</em>, meaning &quot;around.&quot;
                <br />
                Built around your organization
              </p>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                <Link
                  to="/about"
                  className="inline-block text-primary/90 underline-offset-4 hover:underline"
                >
                  About Circa
                </Link>
                <button
                  type="button"
                  className="inline-block text-primary/90 underline-offset-4 hover:underline"
                  onClick={openDialog}
                >
                  Register interest
                </button>
              </div>
            </div>
          </aside>

          <main className="relative z-10 flex flex-col justify-center px-6 py-10 sm:px-10 lg:px-12 xl:px-16">
            <div className="mx-auto w-full max-w-md">
              <div className="mb-8 flex justify-center lg:hidden">
                <CircaWordmark showTagline align="center" />
              </div>

              <div className="mb-8 space-y-2">
                <h2 className="text-3xl font-semibold tracking-tight">
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-sm text-muted-foreground">{subtitle}</p>
                )}
              </div>

              {children}

              {footer && (
                <div className="mt-6 text-center text-sm text-muted-foreground">
                  {footer}
                </div>
              )}

              <p className="mt-4 text-center text-xs text-muted-foreground lg:hidden">
                No invite?{" "}
                <button
                  type="button"
                  className="font-medium text-primary underline-offset-4 hover:underline"
                  onClick={openDialog}
                >
                  Register interest
                </button>
              </p>
            </div>
          </main>

          <div
            aria-hidden
            className="pointer-events-none absolute bottom-0 left-1/2 hidden lg:block"
          >
            <div className="absolute bottom-24 left-1/2 h-64 w-64 -translate-x-1/2 rounded-full border border-primary/20" />
            <div className="absolute bottom-40 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full border border-primary/30" />
          </div>
        </div>
      </div>
    )}
  </ClosedBetaInterestChrome>
);

export default AuthShell;
